import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';

import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PieChartIcon from '@mui/icons-material/PieChart';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';

import ReactGridLayout from 'react-grid-layout';
import Moment from 'moment';
import { SizeMe } from 'react-sizeme';
import {
    get as _get,
    merge as _merge,
    isEmpty as _isEmpty,
} from 'lodash';

import PopupMenu from '../../components/popupmenu';
import ConfirmationDialog from '../../components/confirmationdlg';

import DashboardService from '../../data/dashboard-service';
import MetadataService from '../../data/metadata-service';
import CMCsDataService from '../../data/cmcs-service';
import Auth, { User, ProfileContext } from '../../data/auth-service';
import { GroupsContext, Severities } from '../../data/groups-service';

import DashboardConfig, { PRIVAT, PUBLICW } from './dashboard-config';
import WidgetsSidebar, { WidgetTypes } from './widgets-sidebar';
import Widget, { Wrapper } from './widget';
import { GetDateDesc } from './widgets/components/date-range';
import { GetISODateFromQuick } from '../../libraries/helpers';

import { DEFAULTS as HealthAlertsDefaults } from './widgets/cmc/health-alerts';
import { DEFAULTS as AlertsSeverityDefaults } from './widgets/cmc/alerts-severity';
import { DEFAULTS as AlertsGroupDefaults } from './widgets/cmc/alerts-group';
import { DEFAULTS as AlertsPolicyDefaults } from './widgets/cmc/alerts-policy';
import { DEFAULTS as AlertsActiveTagsDefaults } from './widgets/cmc/alerts-active-tags';
import { DEFAULTS as AlertsTimelineWidgetDefaults } from './widgets/cmc/alerts-timeline';
import { DEFAULTS as MtdtIPActivityDefaults } from './widgets/cmc/mtdt-ip-activity';
import { DEFAULTS as MtdtDNSActivityDefaults } from './widgets/cmc/mtdt-dns-activity';
import { DEFAULTS as MtdtActiveTagsDefaults } from './widgets/cmc/mtdt-active-tags';
import { DEFAULTS as MtdtTimelineDefaults } from './widgets/cmc/mtdt-timeline';
import { DEFAULTS as MtdtGroupDefaults } from './widgets/cmc/mtdt-group';
import { DEFAULTS as UserAgentsWidgetDefaults } from './widgets/cmc/user-agents';
import { DEFAULTS as HealthMetricDefaults } from './widgets/cmc/health-metric';

import { DEFAULTS as MC2AlertsSeverityDefaults } from './widgets/mc2/alerts-severity';
import { DEFAULTS as MC2AlertsCountsDefaults } from './widgets/mc2/alerts-count';
import { DEFAULTS as MC2MtdtCountsDefaults } from './widgets/mc2/mtdt-count';
import { DEFAULTS as MC2MtdtTimelineDefaults } from './widgets/mc2/mtdt-timeline';
import { DEFAULTS as MC2HealthAlertsDefaults } from './widgets/mc2/health-alerts';
import { DEFAULTS as MC2HealthMetricDefaults } from './widgets/mc2/health-metric';

const useStyles = makeStyles(theme => ({
    closedAccordion: {
        display: 'none',
    },
    buttonRight: {
        paddingTop: 20
    },
    root: {
        marginBottom: 50,
    }
}));

const GRID_DEFAULTS = {
    scope: PRIVAT,
    widgets: [],
    options: { snap: { name: 'Snap to Grid', on: true } },
    schedule: {
        quick: 'day',
        interval: 42,
    },
    rowHeight: 25,
    isRounded: true,
};

const WIDGET_DEFAULTS = {
    isDraggable: true,
    isResizable: true,
    resizeHandles: ['se'],
    static: false,
    quick: 'hour',
    start: Moment().subtract(1, 'hours')

    // I wish this worked.
    // React-grid-layout has PR open that will allow passing react-resizable props
    // including lockAspectRatio, but alas no activity since March 2020
    // lockAspectRatio: true,
};

const Dashboard = (props) => {
    const classes = useStyles();
    const theme = useTheme();

    const { perm } = useContext(ProfileContext);
    const toolbarRef = useRef(null);
    const ref = React.createRef();
    const { updateProfile, getProfile, /*perm*/ } = useContext(ProfileContext);

    const [widgetPanel, setWidgetPanel] = useState(false);
    const [dashboards, setDashboards] = useState([]);
    const [myDashboards, setMyDashboards] = useState([]);
    const [dashboard, setDashboard] = useState({});
    const [configOpen, setConfigOpen] = useState(false);
    const [groupKeys, setGroupKeys] = useState([]);
    const [cmcs, setCmcs] = useState([]);
    const [layout, setLayout] = useState([]);
    const [deleteConfrm, setDeleteConfrm] = useState(false);
    const dashPerms = Auth.permissions.get('Dashboards');
    const metadataPerms = Auth.permissions.get('Metadata');

    const handleToggleWidgetPanel = open => {
        setWidgetPanel(open);
    };

    const handleDashboardSelection = s => {
        const f = dashboards.findIndex(d => d.uuid === s.uuid);
        if (f < 0) return;
        setDashboard(dashboards[f]);
        updateProfile('dashboard', { ...getProfile('dashboard'), favorite: s.uuid });
    };

    const getNewTitle = () => {
        let postfix = 0;
        let newtitle = 'My Dashboard';
        while ((dashboards || []).find(d => d.title === newtitle)) {
            postfix++;
            newtitle = `My Dashboard_${postfix}`;
        }

        return newtitle;
    };

    const handleOnConfig = useCallback(() => {
        setConfigOpen(current => {
            if (current) return false;
            return dashboard;
        });
    }, [dashboard]);

    const handleNewDashboard = () => {
        const title = getNewTitle();
        setConfigOpen(_merge({
            ...GRID_DEFAULTS,
            title: title,
            owner: User().username,
        }));
    };

    const handleDeleteDashboard = () => {
        setConfigOpen(false);
        setDeleteConfrm(false);
        const idx = dashboards.findIndex(d => d.uuid === dashboard.uuid);
        if (idx >= 0) dashboards.splice(idx, 1);
        setDashboard(dashboards[0]);
        setDashboards([...dashboards]);
        setMyDashboards(dashboards
            .filter(d => User().username === d.owner)
            .map(d => d.title));
        DashboardService.delete(dashboard.uuid);
    };

    const fetchDashboards = async (favorite) => {
        DashboardService.list().then(d => {
            if (!d || d.length < 1) {
                // This user has no dashboards, let's create a blank one
                if (dashPerms.write) handleNewDashboard();
            } else {
                const usr = User().username;
                // sort dashboards by owner (mine first) then title
                d.sort((a, b) => (
                    a.owner === b.owner ? a.title - b.title
                        : a.owner === usr ? -1
                            : b.owner === usr ? 1
                                : a.owner - b.owner
                ));
                setDashboards([...d]);
                setMyDashboards(d
                    .filter(d => usr === d.owner)
                    .map(d => d.title));
                const i = d.findIndex(d => d.uuid === favorite);
                // setCurrent(i >= 0 ? i : 0);
                setDashboard(d[i >= 0 ? i : 0]);
            }
        }).catch(handleError);
    };

    const onLayoutChange = (layout) => {
        if (layout.length !== dashboard.widgets.length) {
            console.log('some widgets didnt make it back to the layout. permission changed?');
        }
        // map from layout -> widgets to update order
        dashboard.widgets = layout.map((l, idx) => {
            const widget = dashboard.widgets.find(w => `${w.id}` === l.i);
            if (!widget) {
                console.log('cant find widget');
                return l;
            }
            const { x, y, w, h } = l;
            // don't need grid attributes (except for size and location)
            const {
                i,
                edit,
                minW,
                minH,
                isDraggable,
                isResizable,
                resizeHandles,
                static: s,
                ...rest
            } = widget;
            // don't stash edit info
            return { ...rest, ...{ x, y, w, h } };
        }).sort((a, b) => a.y - b.y || a.x - b.x);

        setDashboard(dashboard);
        isWritable() && DashboardService.update(dashboard);
    };

    const typeDefaults = w => {
        switch (w.type.key) {
            case 'brc-widget-alert-severity': return AlertsSeverityDefaults(w.edit);
            case 'brc-widget-alert-group': return AlertsGroupDefaults(w.edit);
            case 'brc-widget-alert-policy': return AlertsPolicyDefaults(w.edit);
            case 'brc-widget-active-alert-tags': return AlertsActiveTagsDefaults(w.edit);
            case 'brc-widget-alert-timeline': return AlertsTimelineWidgetDefaults(w.edit);
            case 'brc-widget-active-metadata-tags': return MtdtActiveTagsDefaults(w.edit);
            case 'brc-widget-dns-activity': return MtdtDNSActivityDefaults(w.edit);
            case 'brc-widget-mtdt-timeline': return MtdtTimelineDefaults(w.edit);
            case 'brc-widget-mtdt-ip-activity': return MtdtIPActivityDefaults(w.edit);
            case 'brc-widget-mtdt-group': return MtdtGroupDefaults(w.edit);
            case 'brc-widget-health-alerts': return HealthAlertsDefaults(w.edit);
            case 'brc-widget-user-agents': return UserAgentsWidgetDefaults(w.edit);
            case 'brc-widget-health-metric': return HealthMetricDefaults(w.edit);

            case 'brc-widget-mc2-alert-severity': return MC2AlertsSeverityDefaults(w.edit);
            case 'brc-widget-mc2-alert-counts': return MC2AlertsCountsDefaults(w.edit);
            case 'brc-widget-mc2-mtdt-counts': return MC2MtdtCountsDefaults(w.edit);
            case 'brc-widget-mc2-mtdt-timeline': return MC2MtdtTimelineDefaults(w.edit);
            case 'brc-widget-mc2-health-alerts': return MC2HealthAlertsDefaults(w.edit);
            case 'brc-widget-mc2-health-metric': return MC2HealthMetricDefaults(w.edit);
        }
        return {};
    };

    // pass force = true to reposition the widgets
    const getLayout = force => {
        const widgets = dashboard.widgets || [];
        return widgets.filter(w => {
            if (!w.type) return false;
            const type = WidgetTypes[w.type.key];
            if (!type) return false;

            // allow match on any perm. granular perms are checked in the widget
            return perm(type.perms);
        }).map((w, i) => {
            w.type = WidgetTypes[w.type.key];
            const typeDefault = typeDefaults(w);
            w.title = w.title || w.type.title;
            // backward compatibility - figure out grid position from old style sizes
            if (w.w === undefined) {
                const oldw = _get(w, 'settings.width', 0);
                w.w = Math.max(Math.floor(oldw / 80), typeDefault.minW);
            }
            if (w.h === undefined) {
                const oldh = _get(w, 'settings.height', 0);
                w.h = Math.max(Math.floor(oldh / 150), typeDefault.minH);
            }
            if (force || w.x === undefined || w.y === undefined) {
                if (i === 0) {
                    w.x = w.x || 0;
                    w.y = w.y || 0;
                } else {
                    const prev = widgets[i - 1];
                    w.x = prev.x + prev.w + w.w > 12 ? 0 : prev.x + prev.w;
                    w.y = prev.x + prev.w + w.w > 12 ? prev.y + w.h : prev.y;
                }
            }
            w.i = `${w.id}`;
            // if editing, set config min size, otherwise use widget defaults

            // if we're overriding dates, will use w.start, w.end, w.quick
            const dates = dashboard.schedule.date_override ? {}
                : {
                    start: dashboard.schedule.quick
                        ? GetISODateFromQuick(dashboard.schedule.quick)
                        : dashboard.schedule.start,
                    end: dashboard.schedule.quick ? undefined : dashboard.schedule.end,
                    quick: dashboard.schedule.quick
                };

            const perms = isWritable() ? {}
                : { isDraggable: false, isResizable: false, static: true };

            // this will be the widget props.data object
            const widgetData = {
                ...WIDGET_DEFAULTS,
                ...w,
                ...typeDefault,
                isDraggable: !w.edit,
                ...dates,
                date_override: dashboard.schedule.date_override,
                ...perms,
            };
            return widgetData;
        });
    };

    const handleEditWidget = widget => {
        const i = (dashboard.widgets || []).findIndex(w => w.id === widget.id);
        if (i < 0) {
            console.log('cannot find widget', widget);
            return;
        }
        const w = dashboard.widgets[i];
        w.edit = true;

        // stash current size and expand the widget or restore previous size
        const d = typeDefaults(w);
        dashboard.widgets[i] = {
            ...w,
            ...{ tw: w.w, th: w.h, tx: w.x, ty: w.y, w: d.minW, h: d.minH },
        };
        setLayout([...getLayout()]);
    };

    const handleFinishEditWidget = widget => {
        const idx = (dashboard.widgets || []).findIndex(w => w.id === widget.id);
        if (idx < 0) {
            console.log('cannot find widget', widget);
            return;
        }
        // Check if this is a new widget being cancelled
        // New widgets have tw, th, tx, ty as undefined (they weren't saved before)
        const currentWidget = dashboard.widgets[idx];
        const isNewWidget = currentWidget.tw === undefined && currentWidget.th === undefined;

        if (isNewWidget && widget.edit === false) {
            // This is a new widget being cancelled - remove it entirely
            dashboard.widgets.splice(idx, 1);
        } else {
            // This is an existing widget - restore previous size and exit edit mode
            const { tw, th, tx, ty, edit, minW, minH, i, ...rest } = widget;
            dashboard.widgets[idx] = { ...rest, ...{ w: tw, h: th, x: tx, y: ty }, edit: false };
        }
        setLayout([...getLayout()]);
    };

    const handleUpdateWidget = widget => {
        const idx = (dashboard.widgets || []).findIndex(w => w.id === widget.id);
        if (idx < 0) {
            console.log('cannot find widget', widget);
            return;
        }
        dashboard.widgets[idx] = { ...dashboard.widgets[idx], ...widget };
        const layout = getLayout(true);
        setLayout([...layout]);
    };

    const handleDeleteWidget = widget => {
        const idx = (dashboard.widgets || []).findIndex(w => w.id === widget.id);
        if (idx < 0) {
            console.log('cannot find widget', widget);
            return;
        }
        dashboard.widgets.splice(idx, 1);
        const layout = getLayout(true);
        setLayout([...layout]);
    };

    const handleAddWidget = t => {
        const { icon, ...rest } = t;
        const widget = {
            id: new Date().getTime(), // unique enough
            title: rest.title,
            edit: true,
            type: rest,
            settings: {
                json_filter: { operator: 'And', nodes: [] }
            },
            // this will allow the widget to show up in the old angular app
            html: `<${t.key} widget="widget" globals="::globals"></${t.key}>`
        };
        const d = typeDefaults({ ...widget, edit: false });
        const ed = typeDefaults(widget);
        dashboard.widgets.push({
            ...widget,
            // Don't set tw, th, tx, ty for new widgets - this marks them as new
            ...{ w: ed.minW, h: ed.minH, ...ed },
        });
        const layout = getLayout(true);
        setLayout([...layout]);
    };

    // const handleUpdateConfig = ({ scope, interval, title, report, schedule, layout }) => {
    const handleUpdateConfig = useCallback((d, oldtitle) => {
        setConfigOpen(false);
        if (d.uuid) {
            DashboardService.update(d);
            setDashboards(current => {
                const idx = (current || []).find(di => di.uuid === d.uuid);
                if (idx >= 0) current[idx] = d;
                return [...current];
            });
            setMyDashboards(current => {
                const idx = (current || []).find(di => di === oldtitle);
                if (idx >= 0) current[idx] = d.title;
                return [...current];
            });
            setDashboard({ ...d });
        } else {
            DashboardService.create(d).then(res => {
                setDashboards(current => {
                    current.push(res);
                    return [...current];
                });
                setMyDashboards(current => {
                    current.push(res.title);
                    return [...current];
                });
                setDashboard(res);
            });
        }
    }, []);

    const handlePrintDashboard = () => {
        DashboardService.print(dashboard);
    };

    const getCompactType = () => ['vertical', 'horizontal']
        .find(c => c === _get(dashboard, 'layout.compact_type')) || 'vertical';

    const getDateDesc = () => {
        const override = _get(dashboard, 'schedule.date_override', false);
        if (override) return '';
        return GetDateDesc(
            _get(dashboard, 'schedule.quick'),
            _get(dashboard, 'schedule.start'),
            _get(dashboard, 'schedule.end'),
            theme.date.long);
    };

    const isOwner = () => User().username === dashboard.owner;
    const isWritable = () => dashPerms.write ? isOwner() || dashboard.scope === PUBLICW : false;

    const fetchFields = async () => {
        if (metadataPerms.read) {
            MetadataService.cacheFields(true).catch(handleError);
            Object.entries(cmcs || { undefined: '' })
                .map(([uuid, name]) => ({ uuid, name }))
                .forEach(cmc => { MetadataService.cacheFields(true, cmc) });
        }
    };

    const fetchGroups = async () => {
        // add other groups here (sensors) for consistent colors across widgets
        setGroupKeys(Array.from(new Set([...Severities])));

        // mc2 dashboard want consistent colors for cmc across widgets
        if (process.env.BRICATA_APPLIANCE_TYPE === 'mc2') {
            CMCsDataService.getNames().then(cmcs => {
                setGroupKeys(current => current.concat(Object.values(cmcs)));
                setCmcs(Object.entries(cmcs).map(([uuid, name]) => ({ uuid, name })));
            });
        }
    };

    const handleError = (error) => {
        console.error(error);
        toast.error(
            'There was an error processing your request.',
            { toastId: 'dashboard' }
        );
    };

    // initial hook
    useEffect(() => {
        const profile = getProfile('dashboard') || {};
        fetchDashboards(profile.favorite);
        fetchFields();
        fetchGroups();

        return () => {
            DashboardService.stop();
        };
    }, []);

    // initial hook
    useEffect(() => {
        fetchFields();
    }, [cmcs]);

    useEffect(() => {
        if (!dashboard) return;
        setLayout([...getLayout()]);

        if (dashboard.schedule) {
            DashboardService.start(dashboard.schedule.interval);
        }
    }, [dashboard]);

    return (
        <GroupsContext.Provider value={{ groupKeys, cmcs }}>
            <div className={classes.root}>
                {isWritable() && <WidgetsSidebar
                    perm={perm}
                    anchor="left"
                    header="Add a Widget"
                    open={widgetPanel}
                    onToggleClose={handleToggleWidgetPanel}
                    onAddWidget={handleAddWidget}/>}
                <Grid container spacing={1} direction="row" justifyContent="space-between">
                    <Grid item>
                        <Typography variant="h5">{dashboard.title}
                            <PopupMenu
                                tooltip="select/add dashboard"
                                items={dashboards}
                                itemkey={'uuid'}
                                onSelect={handleDashboardSelection}
                                selection={dashboard.uuid}
                                itemTitle={'title'}
                                icon={<ArrowDropDownIcon fontSize='large' color='secondary'/>}
                                getInfo={d => d.owner}
                                moreActions={[
                                    dashPerms.write && {
                                        icon: <AddIcon fontSize="small" color="primary"/>,
                                        title: 'Add New Dashboard',
                                        key: 'new',
                                        callback: handleNewDashboard,
                                        divider: true,
                                    },
                                ]}/>
                            {isWritable() && <Button
                                color="primary"
                                aria-label="add widget"
                                onClick={() => handleToggleWidgetPanel(true)}
                                startIcon={<PieChartIcon />}> Add Widget
                            </Button>}
                        </Typography>
                        <Typography variant="body2">
                            {getDateDesc()}
                        </Typography>
                    </Grid>
                    {/* time range controls get pulled right */}
                    <Grid item>
                        <Grid container direction="row" justifyContent="flex-end">
                            <Grid item>
                                {!_isEmpty(dashboard) && <Typography style={{ paddingTop: 20 }}>
                                    Owned by &nbsp;
                                    <Box component="span" fontWeight='fontWeightBold'>
                                        {dashboard.owner}  &nbsp;
                                    </Box>
                                    for  &nbsp;
                                    <Box component="span" fontWeight='fontWeightBold'>
                                        {dashboard.scope}  &nbsp;
                                    </Box>
                                    access
                                </Typography>}
                            </Grid>
                            <Grid item>
                                {isWritable() &&
                                    <IconButton
                                        title="dashboard settings"
                                        className={classes.buttonRight}
                                        aria-label="dashboard settings"
                                        onClick={handleOnConfig}
                                        size="large">
                                        <SettingsIcon />
                                    </IconButton>
                                }
                                {isWritable() && dashboards.length > 1 && isOwner() &&
                                    <IconButton
                                        title="delete dashboard"
                                        className={classes.buttonRight}
                                        aria-label="delete dashboard"
                                        onClick={() => setDeleteConfrm(true)}
                                        size="large">
                                        <DeleteIcon />
                                    </IconButton>
                                }
                                {!_isEmpty(dashboard) && <IconButton
                                    title="print dashboard"
                                    className={classes.buttonRight}
                                    aria-label="print dashboard"
                                    onClick={handlePrintDashboard}
                                    size="large">
                                    <PrintIcon />
                                </IconButton>}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                <ConfirmationDialog
                    onClose={() => setDeleteConfrm(false)}
                    onOK={handleDeleteDashboard}
                    open={deleteConfrm}
                    message={`Delete '${dashboard.title}'?`}/>

                <Grid item xs={12} ref={toolbarRef}>
                    <Box mb={2}>
                        {configOpen && (configOpen.owner === User().username || isWritable()) &&
                            <DashboardConfig
                                names={myDashboards}
                                dashboard={configOpen}
                                show={configOpen !== false}
                                onCancel={() => setConfigOpen(false)}
                                onOK={handleUpdateConfig}
                                anchorEl={toolbarRef.current}/>}
                    </Box>
                </Grid>
                <div>
                    <SizeMe>
                        {({ size }) =>
                            <ReactGridLayout
                                layout={layout}
                                width={size.width}
                                rowHeight={GRID_DEFAULTS.rowHeight}
                                onLayoutChange={onLayoutChange}
                                measureBeforeMount={false}
                                useCSSTransforms={false}
                                compactType={getCompactType()}
                                preventCollision={false}
                                draggableHandle={'.drag-handle'}>
                                {layout.map((d, i) => {
                                    return <Wrapper key={d.i} data={d} data-grid={d}>
                                        <Widget
                                            ref={ref}
                                            data-grid={d}
                                            data={d}
                                            onEdit={handleEditWidget}
                                            onFinishEdit={handleFinishEditWidget}
                                            onUpdateWidget={handleUpdateWidget}
                                            onDelete={handleDeleteWidget}/>
                                    </Wrapper>;
                                })}
                            </ReactGridLayout>
                        }
                    </SizeMe>
                </div>
            </div>
        </GroupsContext.Provider>
    );
};
export default Dashboard;
